import { FC, ReactNode } from 'react';
import { Control, FieldPath } from 'react-hook-form';

import { ChevronDownIcon } from '../../../assets/icons/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../components/combobox/index';
import { getTranslation } from '../../../utils/get-translation.util';
import { AddDividendFormData } from './add-dividend.validation';

type Option = { label: string; value: number | string };

type Props = {
  control: Control<AddDividendFormData>;
  disabled?: boolean;
  /** Shown inside the dropdown when nothing is selectable — an empty list reads as a dead field. */
  emptyMessage?: ReactNode;
  footerAction?: ReactNode;
  name: FieldPath<AddDividendFormData>;
  onSelect?: (value: number | string) => void;
  options: Option[];
  placeholder: ReactNode;
};

// Floating-label select built on the shared Combobox primitives (matches the design system).
export const DividendSelectField: FC<Props> = ({
  control,
  disabled,
  emptyMessage,
  footerAction,
  name,
  onSelect,
  options,
  placeholder,
}) => (
  <FormCombobox
    // The panel is absolutely positioned, so the field itself has to be the positioned ancestor —
    // otherwise `w-full` measures some outer card and the dropdown stops matching the input.
    className="relative w-full"
    control={control}
    name={name as never}
    onSelect={(value) => onSelect?.(value as number | string)}
  >
    <div className="relative">
      <FormComboboxInput
        control={control}
        customValue={(value) =>
          options.find((option) => option.value === value)?.label ??
          (value ? undefined : getTranslation('PLEASE_SELECT'))
        }
        disabled={disabled}
        icon={<ChevronDownIcon className="size-4" />}
        name={name as never}
        placeholder={placeholder}
        readOnly
      />
      {!disabled && <ComboboxButton className="absolute inset-0 z-1 size-full cursor-pointer" />}
    </div>
    {/* Tighter than the shared default — the dividend dropdowns list short values (86cayfppu). */}
    <ComboboxOptions className="w-full gap-0.5 p-1">
      {options.length === 0 && (
        <>
          {emptyMessage && (
            <span className="block px-3 py-1.5 text-sm text-gray-500">{emptyMessage}</span>
          )}
          {footerAction}
        </>
      )}
      {options.map((option) => (
        <FormComboboxOption
          control={control}
          key={String(option.value)}
          name={name as never}
          value={option.value as never}
        >
          <span className="block text-sm text-gray-700">{option.label}</span>
        </FormComboboxOption>
      ))}
    </ComboboxOptions>
  </FormCombobox>
);
